<template>
    <div>
        <validation-observer ref="createBeneficialOwnerValidation">
            <validation-provider
                v-slot="{ errors }"
                name="id"
                rules="required"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="id-row" for="id">
                            ID
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-popover
                        target="id-row"
                        triggers="hover"
                        placement="right"
                        variant="black"
                    >
                        Enter your NRIC or FIN if you are a Singapore resident.
                        <br />
                        Enter your passport number if you are a foreigner.
                    </b-popover>

                    <b-form-input
                        id="id"
                        v-model="beneficialOwner.NRIC"
                        placeholder="Enter NRIC or passport"
                        :state="errors.length > 0 ? false : null"
                        data-test="beneficial-owner-id"
                        @input="requestAutoFillFormData"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="beneficial owner name"
                rules="required"
            >
                <b-form-group label="Legal name">
                    <b-form-input
                        id="name"
                        v-model="beneficialOwner.name"
                        placeholder="Enter beneficial owner name"
                        :state="errors.length > 0 ? false : null"
                        data-test="beneficial-owner-name"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="ID type"
                rules="required"
                class="end-align-indicator-long-select"
            >
                <b-form-group
                    label="ID type"
                    :state="errors.length > 0 ? false : null"
                >
                    <v-select
                        ref="idType"
                        v-model="beneficialOwner.idType"
                        class="custom-select-icon"
                        :clearable="false"
                        placeholder="Select ID type"
                        label="name"
                        :options="idType_options"
                        @input="handleNationalityOnIdTypeChanged"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="nationality"
                rules="required"
                class="end-align-indicator-long-select"
            >
                <b-form-group label="Nationality" :state="errors.length > 0 ? false : null">
                    <v-select
                        ref="nationality"
                        v-model="beneficialOwner.nationality"
                        class="custom-select-icon"
                        :clearable="false"
                        placeholder="Select nationality"
                        label="text"
                        :disabled ="shouldDisabledNationality"
                        :options="nationality_options"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-if="shouldShowExpiryDate"
                v-slot="{ errors }"
                name="date of expiry"
                rules="required"
            >
                <b-form-group :state="errors.length > 0 ? false : null">
                    <slot name="label">
                        <label id="dateOfExpiry" for="id">
                            Date of expiry
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-popover
                        target="dateOfExpiry"
                        triggers="hover"
                        placement="right"
                        variant="black"
                    >
                        Skip this if you are a Singapore citizen or permanent
                        resident.
                        <br />
                        Fill in the date of expiry of your passport or FIN if
                        you are a foreigner.
                    </b-popover>

                    <flat-pickr
                        v-model="beneficialOwner.dateOfExpiry"
                        placeholder="Enter date of expiry"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-picker-dateOfExpiry"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="date of birth"
                rules="required"
            >
                <b-form-group label="Date of birth" :state="errors.length > 0 ? false : null">
                    <flat-pickr
                        v-model="beneficialOwner.dob"
                        placeholder="Enter date of birth"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-picker-dob"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="number of shares"
                rules="required"
            >
                <b-form-group label="Number of shares">
                    <b-form-input
                        v-model="beneficialOwner.numberOfShare"
                        v-mask="shareMask"
                        placeholder="Enter number of shares"
                        :state="errors.length > 0 ? false : null"
                        data-test="beneficial-owner-shares"
                        type="text"
                        no-wheel
                        @keydown.up.prevent
                        @keydown.down.prevent
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="email address"
                rules="required|email"
            >
                <b-form-group label="Email address">
                    <b-form-input
                        v-model="beneficialOwner.email"
                        placeholder="Enter beneficial owner email address"
                        :state="errors.length > 0 ? false : null"
                        data-test="beneficial-owner-email"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="contact number"
                rules="required"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="contactNumber" for="id">
                            Contact number
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-popover
                        target="contactNumber"
                        triggers="hover"
                        placement="right"
                        variant="black"
                    >
                        Enter your Singapore mobile number if you are a
                        Singapore resident.
                        <br />
                        Enter your overseas mobile number if you are a
                        foreigner.
                    </b-popover>

                    <b-form-input
                        v-model="beneficialOwner.contactNumber"
                        placeholder="Enter beneficial owner contact number"
                        :state="errors.length > 0 ? false : null"
                        data-test="beneficial-owner-contact_number"
                        type="tel"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <!-- Source of Fund -->
            <div class="mt-2">
                <b-form-group label="Please select all the sources for your company’s funds.">
                    <validation-provider
                        v-slot="{ errors }"
                        name="source of fund"
                        rules="required"
                    >
                        <b-form-checkbox-group
                            id="checkbox-group-2"
                            ref="sourceOfFund"
                            v-model="selectedSourcesOfFunds"
                            name="sources"
                            stacked
                            :options="sourceOfFundsOption"
                        >
                        </b-form-checkbox-group>
                        <div v-if="errors[0]" class="alert-dismissable alert alert-danger mt-1 p-1">
                            {{ errors[0] }}
                        </div>
                    </validation-provider>
                </b-form-group>

                <b-form-group v-if="checkOtherSourceOfFundOption">
                    <validation-provider
                        v-slot="{ errors }"
                        name="other sources of fund"
                        rules="required"
                    >
                        <b-form-textarea
                            id="funding_other_reasons"
                            ref="otherSource"
                            v-model="beneficialOwner.otherSource"
                            name="other_source"
                            rows="3"
                            max-rows="3"
                            placeholder="Enter your other source here."
                            data-test="other-source"
                        ></b-form-textarea>

                        <div v-if="errors[0]" class="alert-dismissable alert alert-danger mt-1 p-1">
                            {{ errors[0] }}
                        </div>
                    </validation-provider>
                </b-form-group>
            </div>
            <!-- End Source of fund -->

            <validation-provider
                v-slot="{ errors }"
                name="address"
                rules="required"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="address" for="id">
                            Address
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-popover
                        target="address"
                        triggers="hover"
                        placement="right"
                        variant="black"
                    >
                        Enter Singapore address if you are a Singapore resident.
                        <br />
                        Enter your overseas address if you are a foreigner.
                    </b-popover>

                    <b-form-textarea
                        id="textarea"
                        v-model="beneficialOwner.address"
                        placeholder="Enter beneficial owner address"
                        :state="errors.length > 0 ? false : null"
                        data-test="beneficial-owner-address"
                        rows="3"
                        max-rows="3"
                    ></b-form-textarea>
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="shareholder"
                rules="required"
                class="end-align-indicator-long-select"
            >
                <b-form-group label="Shareholder" :state="errors.length > 0 ? false : null">
                    <v-select
                        ref="shareholder"
                        v-model="beneficialOwner.shareholder"
                        class="custom-select-icon"
                        :clearable="false"
                        placeholder="Select shareholder"
                        label="name"
                        :disabled ="isDisabledShareholder"
                        :options="shareholderOptions"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>
        </validation-observer>

        <b-button
            block
            class="mt-2"
            variant="primary"
            data-test="create-beneficial-owner-btn"
            @click="createOrUpdate"
            >{{ status === "create" ? "Add" : "Update" }}
            <img
                v-if="status === 'create'"
                :src="'/illustrations/add-circle.svg'"
                class="float-right"
                alt="add-logo"
            />
            <img
                v-if="status === 'update'"
                :src="'/illustrations/edit.svg'"
                class="float-right"
                alt="edit-logo"
            />
        </b-button>

        <b-button
            block
            class="mb-3"
            variant="outline-primary"
            @click="$emit('cancel')"
            >Cancel
            <vector 
                data-type="icons" 
                data-width="15px" 
                data-height="15px" 
                data-name="cancel" 
                class="float-right"
                alt="cancel-logo"
            />
        </b-button>
    </div>
</template>

<script>
import {
    BFormGroup,
    BFormInput,
    BButton,
    BFormTextarea,
    BPopover,
    BFormCheckboxGroup,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import { debounce } from 'debounce';
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import IdType from "@/models/IdType";
import Nationality from "@/models/Nationality";
import Country from "@/models/Country";
import User from '@/models/User';
import { NRIC_CITIZEN_ID, SINGAPORE_CITIZEN } from "@/constants/config";
import { formatDate, shareMask, unMask } from "@/helpers";
import { getSourceOfFundsAndReasonsToIncorporate } from "@/apis/company";

export default {
    components: {
        BFormGroup,
        BFormInput,
        BButton,
        BFormTextarea,
        ValidationObserver,
        ValidationProvider,
        flatPickr,
        vSelect,
        BPopover,
        BFormCheckboxGroup,
    },

    props: {
        dataStatus: {
            required: false,
            default: "create",
            type: String,
        },

        dataBeneficialOwner: {
            required: true,
            type: Object,
        },

        shareholderOptions: {
            required: true,
            type: Array,
        },
    },

    data() {
        return {
            idToIgnoreDateOfExpiry: null,
            idTips: "Tips For Id",
            required,
            email,
            status: this.dataStatus,
            beneficialOwner: {
                // eslint-disable-next-line no-underscore-dangle
                _id: this.dataBeneficialOwner._id,
                name: this.dataBeneficialOwner.name,
                idType: this.dataBeneficialOwner?.idType,
                NRIC: this.dataBeneficialOwner.NRIC,
                dateOfExpiry: this.dataBeneficialOwner.dateOfExpiry,
                dob: this.dataBeneficialOwner.dob,
                numberOfShare: this.dataBeneficialOwner.numberOfShare,
                nationality: this.dataBeneficialOwner.nationality,
                email: this.dataBeneficialOwner.email,
                contactNumber: this.dataBeneficialOwner.contactNumber,
                sourcesOfFunds: this.dataBeneficialOwner.sourcesOfFunds,
                otherSource: this.dataBeneficialOwner.otherSource,
                address: this.dataBeneficialOwner.address,
                shareholder: this.dataBeneficialOwner.shareholder,
            },
            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            nationality_options: [],
            idType_options: [],
            sourceOfFundsOption: [],
            selectedSourcesOfFunds: [],

            maskKeys: ['numberOfShare'],
            shareMask,
        };
    },

    computed: {
        shouldShowExpiryDate()
        {
            if(this.beneficialOwner.idType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.beneficialOwner.dateOfExpiry = null;
            }
            return ! this.beneficialOwner.idType?.name.includes("NRIC");

            // if (! this.beneficialOwner.nationality)
            //     return true;

            // if (this.idToIgnoreDateOfExpiry)
            // {
            //     if (this.beneficialOwner.nationality && typeof this.beneficialOwner.nationality === "number") 
            //         return ! this.idToIgnoreDateOfExpiry.includes(this.beneficialOwner.nationality);
            
            //     if (this.beneficialOwner.nationality && typeof this.beneficialOwner.nationality !== "number") 
            //         return ! this.idToIgnoreDateOfExpiry.includes(this.beneficialOwner.nationality.value);
            // }

            // return true;
        },

        shouldDisabledNationality() {
            if(this.beneficialOwner.idType && this.beneficialOwner.idType.id === NRIC_CITIZEN_ID)
                return true;
            return false; 
        },

        checkOtherSourceOfFundOption() {
            if (this.selectedSourcesOfFunds.length >= 1) {
                const otherSource = this.sourceOfFundsOption.find(
                    reason => reason.text.search(/^Others/i) === 0
                );
                
                if(otherSource) 
                    return this.selectedSourcesOfFunds.includes(otherSource.value);
            }

            return false;
        },

        isDisabledShareholder() {
            return this.dataStatus !== 'create' && this.beneficialOwner.email === this.beneficialOwner.shareholder.email;
        }
    },

    async created() {
        // !! need to load source of funds first
        // source of funds
        getSourceOfFundsAndReasonsToIncorporate()
            .then(sof => {
                this.sourceOfFundsOption = this.getSourceOfFunds(sof);
                if(Array.isArray(this.beneficialOwner.sourcesOfFunds)) {
                    this.selectedSourcesOfFunds = [...new Set([...this.selectedSourcesOfFunds, ...this.beneficialOwner.sourcesOfFunds])];
                }
            })
            .catch(e => {
                console.log("Error while trying to source of funds", e);
            });
            
        const data = await Nationality.all();
        this.idType_options = await IdType.all();

        // get ignore ids for date of expiry, if nationality is singaporean
        this.idToIgnoreDateOfExpiry = await Country.getSingaporeanNationality();

        this.nationality_options = data.map(obj => {
            const value = obj.id;
            const text = obj.name;
            return { value, text };
        });
    },

    methods: {
        getSourceOfFunds(payload) {
            return payload
                .filter(info => info.type === "Source")
                .map(data => ({
                    text: data.description,
                    value: data.id,
                }));
        },

        handleNationalityOnIdTypeChanged(data) {
            if(data.id === NRIC_CITIZEN_ID) {
                const singaporeCitizen = this.nationality_options.filter(
                    nationality => nationality.text === SINGAPORE_CITIZEN
                ).map(
                    nation => ({
                        text: nation.text,
                        value: nation.value
                    })
                )[0];

                this.beneficialOwner.nationality = singaporeCitizen;
            }
        },

        // eslint-disable-next-line func-names
        requestAutoFillFormData: debounce(async function(id) {
            const user = await User.getByNRIC(id);

            if(user) this.fillBeneficialOwnerData(user);
        }, 500),

        fillBeneficialOwnerData(user) {
            this.beneficialOwner.name = user?.user_info?.legal_name ?? user.name;
            this.beneficialOwner.idType = user.user_info?.id_type
                ? {
                    id: user.user_info.id_type.id,
                    name: user.user_info.id_type.name
                }
                : null;
            this.beneficialOwner.nric = user.user_info?.NRIC;
            this.beneficialOwner.nationality = user.user_info?.nationality
                ? { 
                    value: user.user_info.nationality.id,
                    text: user.user_info.nationality.name 
                }
                : null;
            this.beneficialOwner.dateOfExpiry = formatDate(user.user_info?.date_of_expiry);
            this.beneficialOwner.dob = formatDate(user.user_info?.DOB);
            this.beneficialOwner.email = user.email;
            this.beneficialOwner.contactNumber = user.user_info?.contact_number;
            this.beneficialOwner.address = user.user_info?.address;
        },
        
        createOrUpdate() {
            this.$refs.createBeneficialOwnerValidation
                .validate()
                .then(async success => {
                    if (success) {
                        const beneficialOwner = unMask(this.beneficialOwner, this.maskKeys);
                        beneficialOwner.sourcesOfFunds = this.selectedSourcesOfFunds;

                        this.$emit("createOrEdit", {
                            ...beneficialOwner,
                            nationality: this.beneficialOwner.nationality,
                        });
                        this.$emit("cancel");
                    }
                });
        },
    },
};
</script>
